import { ec, ecId, ecLean, handBack, record, type JanusCtx, type Row } from '../ability/context';
import { patchVitalityCluster } from '../compile/held-vitality';
import { readInventoryField, readInventoryRows, readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { readItemField } from '../compile/item-field-reader';
import { fromBaseUuid } from '../compile/from-base-context';
import { combatCenter } from '../actions/ability-preconditions';
import { applyStatusEffect } from '../ability/statuses/apply-status-effect';
import { getStatusEffectDurationTimes } from '../ability/statuses/get-status-effect-duration-times';
import type { JanusInput } from '../ability/janus';
import { storedStatusHasSpecialTeardown } from '../ability/statuses/status-effect-removal-helpers';
import { statusItemId, statusCasterId, statusRows, statusRemovalBefore, STATUS_IDS } from '../statuses/expiry';
import { statusScalar } from '../actions/activity';
import { stackTarget } from '../inventory/move-rules';
import { EQUIPMENT_SLOTS } from '../inventory/equipment-slots';
import { emptySpawnPlan, mergeSpawnPlans, type SpawnReaders, type SpawnRowPlan } from '../spawn/row-plan';
import { classifyKillOrVanish } from './classification';
import { corpseTemplateId, planCorpseMint } from './corpse';
import { monsterBitDrops, planMonsterBits } from './monster-bits';
import { merchantDeathPictures, planMerchantDrops } from './merchant';
import { settleTurnDeath } from './turn-rows';
import { deathStatusRows, dyingStatusConnections, endOrphanedConcentration, releaseDyingCasterStatuses, resolveDeathStatusCleanup, type DeathStatusExecutor } from './cleanup';
import { createDeathStatusExecutor, deathRemovalState } from './status-removal';
import { isAnimatedSummon, isHunterSummon, isOrdinarySummon, summonControllerId, summonsOnStatusRemoved, triggerSummonDeath } from './summons';
import { deathHolders, planDepartureRows } from './departure';
import { encounterField } from '../../shared/array-types';
import { createDeathGrounding, type DeathLanding } from './grounding';
import { grantControllerMenace } from './controller-menace';
import { DEAD_STATUS_ID, DEATH_STATE_SOURCE_IDS, HASTED_STATUS_ID, STUNNED_STATUS_ID } from './state-ids';
import { deathDutyName, skipDeathDuty } from './skip';
import { statusCapabilityFailure } from '../ability/statuses/status-capability';
import { resolveTemporaryChargeRemovals } from '../statuses/exact-removal-tails';
import { isOperatorStatus } from '../statuses/riding-removal';
import { adjustCharges } from '../ability/charges/adjust-charges';
import { ABILITY_CHARGE_BUCKETS, abilityEntryId } from '../ability/charges/charge-entry';

const CURSE_OF_SACRIFICE_ID = 'C7C0BBD2-2B13-D3D2-6D37-61C3FA77A693';
const GIFT_ID = '30BD0A8F-DC53-4B80-B5C4-8FBB04F9F9C1'; // ClassMechanic 'gift' (warlock)

const PRONE = 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA';
const ROTTING = '62E83DEB-4569-AE3A-F671-FF514C508FD2';
const UNDEAD = '3FEEDE53-20B7-DEE8-C64B-8D0738E667A9';
const PERMANENT = { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] as [number, number, number, number] };

/** Each distinct curse caster gains one Gift while the dying holder's marks are still live. */
function settleSacrifice(ctx: JanusCtx, dying: Row, executor: DeathStatusExecutor): void {
    if (ctx.dryRun) return;
    const casters = new Set<string>();
    for (const status of executor.targetStatuses?.(ecId(dying)) ?? statusRows(dying, 'target')) {
        if (statusItemId(status) !== CURSE_OF_SACRIFICE_ID) continue;
        const casterId = statusCasterId(status);
        if (!casterId || casters.has(casterId)) continue;
        casters.add(casterId);
        const warlock = ctx.members.get(casterId);
        if (!warlock) {
            skipDeathDuty(ctx, dying, 'Curse of Sacrifice: the cursing warlock is not in this encounter, so no Gift is granted.');
            continue;
        }
        // adjust_charges grants nothing without a Gift pool.
        if (ABILITY_CHARGE_BUCKETS.some(bucket => ctx.read.entries(warlock, bucket).some(entry => abilityEntryId(entry) === GIFT_ID)))
            adjustCharges(ctx, warlock, GIFT_ID, 1, true);
    }
}

/** Source expiry kills an animation with its corpse intact; ordinary summons use destroy_creature. */
function sourceSummonDeparture(ctx: JanusCtx, summon: Row, ordinary: () => void): void {
    if (Number(ecLean(summon, 'encounter_creature_dead') ?? 0) || Number(ecLean(summon, 'encounter_creature_deleted') ?? 0)) return;
    if (!isAnimatedSummon(summon)) { ordinary(); return; }
    if (ctx.composedDeaths?.includes(ecId(summon))) return;
    composeActualDeath(ctx, summon, summon, summon, 0, { killMessage: 'creature_died', creditKiller: false, sourceExpiry: true });
    triggerSummonDeath(ctx, summon);
}
export interface DeathResources {
    inventory: Pick<SpawnReaders, 'item' | 'inventory' | 'uuid' | 'clock'>;
    /** First-compile only the final new body/part rows for the applied diff. */
    prepareRows?: (plan: SpawnRowPlan) => SpawnRowPlan;
    casterDeathStatusIds: readonly string[];
    creatureSource?: (id: string) => Row | null;
    /** Optional catalog lookups also probe non-item UUIDs; those must return null. */
    definition?: (id: string) => Row | null;
    land?: (target: Row) => DeathLanding;
}
export const DEATH_STATUS_SOURCE_IDS = [PRONE, ROTTING, '99C862B9-D6AE-4E18-7803-A6F9B42A88E1',
    '8AE129AF-52D8-CCE8-706A-85767DB52740', 'A5CDC77C-D224-DD5F-291B-EF8DF2EA443D', '36C061BF-E480-4334-961C-26500150E3C4',
    'BE42824F-46BE-EC80-AB7F-B823190AB6C8', 'F73BA661-ADD2-26E0-E265-FBEB7E5E6AF1', DEAD_STATUS_ID, STUNNED_STATUS_ID, ...DEATH_STATE_SOURCE_IDS];

/** Resolve source inputs before rolling, without replaying the action with maximum dice. */
export function deathSourceIds(target: Row, read: Pick<SpawnReaders, 'value' | 'entries'>): string[] {
    const pc = String(readItemField(target, 'item_type') ?? ecLean(target, 'item_type') ?? '') === 'player_character';
    return [...new Set([corpseTemplateId(target, read), ...pc ? [] : monsterBitDrops(target, read).map(row => row.itemId), ...DEATH_STATUS_SOURCE_IDS])];
}

/** Share the prepared definition package with ordinary PC Dying as well as true death. */
export function deathResourcesFor(ctx: JanusCtx, target: Row): DeathResources & Pick<ReturnType<typeof createDeathGrounding>, 'drop'> {
    const resources = ctx.read.deathResources?.(ctx, target);
    if (!resources) handBack('death:source-package-not-prepared');
    const definition = ctx.read.definition;
    ctx.read = { ...ctx.read, definition: key => definition(key) ?? (resources.definition
        ? resources.definition(key) : resources.inventory.item(key)) };
    const grounding = createDeathGrounding(ctx);
    return { ...resources, land: resources.land ?? grounding.land, drop: grounding.drop };
}

/** Native in-hand drop at the body's pre-landing position. Shared stack matching preserves the
 * complete row (including children and enchantment); only a merge retires its source identity. */
function dropHands(ctx: JanusCtx, target: Row, plan: SpawnRowPlan, dropPosition = createDeathGrounding(ctx).drop): void {
    const at = combatCenter(target);
    if (!at) handBack('death:drop-position-not-held');
    const encounterId = String(ecLean(target, 'encounter_id'));
    const slots: string[] = [EQUIPMENT_SLOTS.main_hand, EQUIPMENT_SLOTS.off_hand, EQUIPMENT_SLOTS.both_hands];
    const floor = readInventoryRows(ctx.encounter, 'encounter_inventories_arr');
    for (const item of readInventoryRows(target, 'inventory')) {
        const equipped = String(readInventoryField(item, 'equipped') ?? '');
        if (!slots.includes(equipped) || Number(readInventoryField(item, 'inventory_deleted') ?? 0)) continue;
        const drop = dropPosition(item, target);
        const stack = stackTarget(item, floor, { isFloor: true, ...drop }, 0, { value: (row, key) => ctx.read.value(row ?? null, key),
            inventoryField: (row, key) => {
                const axis = key === 'x_pos' ? 'x' : key === 'y_pos' ? 'y' : key === 'z_pos' ? 'z' : undefined;
                return axis && row ? combatCenter(row)?.[axis] : readInventoryField(row ?? null, key);
            },
            itemType: row => String(readItemField(ctx.read.definition(String(readInventoryField(row ?? null, 'inventory_item_id') ?? '')), 'item_type') ?? ''),
            statusItemIds: row => statusRows(row, 'target').map(statusItemId) });
        if ('unknown' in stack) handBack(`death:drop-${stack.unknown}`);
        const inventoryId = String(readInventoryField(item, 'inventory_id'));
        const before = { possess_id: String(readInventoryField(item, 'possess_id') ?? ''), equipped,
            item_qty: Number(readInventoryField(item, 'item_qty') ?? 1), inventory_deleted: Number(readInventoryField(item, 'inventory_deleted') ?? 0) };
        if (stack.target) {
            const destination = floor.find(row => readInventoryField(row, 'inventory_id') === stack.target)!;
            const quantity = Number(readInventoryField(destination, 'item_qty') ?? 1);
            plan.writes.push({ table: 'inventory', op: 'update', row: { inventory_id: stack.target, item_qty: quantity + before.item_qty }, before: { item_qty: quantity } });
            plan.writes.push({ table: 'inventory', op: 'update', row: { inventory_id: inventoryId, inventory_deleted: 1 }, before });
            (destination.inventory as Row).item_qty = quantity + before.item_qty;
        } else {
            const changes = { possess_id: encounterId, equipped: '', inventory_wares: 0, x_pos: drop.x, y_pos: drop.y, z_pos: drop.z };
            plan.writes.push({ table: 'inventory', op: 'update', row: { inventory_id: inventoryId, ...changes }, before });
            Object.assign(record(item.inventory) ?? item, changes);
            item.position = { x_pos: drop.x, y_pos: drop.y, z_pos: drop.z };
            floor.push(item);
        }
        target.inventory = readInventoryRows(target, 'inventory').filter(row => readInventoryField(row, 'inventory_id') !== inventoryId);
        ctx.rows.push({ roll_type_1: 'drop_inventory', roll_type_2: null, roll_item_id: inventoryId,
            roll_by: ecId(target), roll_to: null, roll_result: 0 });
    }
    ctx.encounter.encounter_inventories_arr = floor;
}

/** The same physical drop belongs to a living PC's Dying entry. */
export function dropDyingHands(ctx: JanusCtx, target: Row): void {
    if (ctx.dryRun) return;
    const plan = emptySpawnPlan();
    dropHands(ctx, target, plan);
    if (plan.writes.length) ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, plan);
}

/** The death-status lane: one physical ledger with its effect hooks, and destroy_creature's summon departure. `before` replaces the
 * historical ledger when the removal that reached the summons already ran (departSummon). */
export function deathLane(ctx: JanusCtx, resources: DeathResources | undefined, plan: SpawnRowPlan, before?: readonly Row[], laneOptions: { transportHandled?: boolean } = {}) {
    const holders = deathHolders([ctx.encounter, ...ctx.members.values()]);
    const historical = before ?? deathStatusRows(holders);
    const hasteHandled = new Set<string>();
    const effects = deathRemovalState(ctx, holders, {
        beforeRetire: (holder, status) => {
            const definition = effects.definition(status);
            if (ctx.read.has(definition, 'object_destroyed_after_duration_complete'))
                handBack('death:duration-inventory-destruction-not-ported');
            if (isOperatorStatus(status)) {
                if (!holder) handBack('death:released-status-target-not-held');
                if (Number(ecLean(holder, 'encounter_creature_dead') ?? 0) === 0
                    && Number(ecLean(holder, 'encounter_creature_deleted') ?? 0) === 0)
                    handBack('death:released-transport-support-not-ported');
            }
            // Encounter sources own no charge pool; remote projections only need a
            // held target when the shared resolver finds a temporary grant to retire.
            if (holder !== ctx.encounter) resolveTemporaryChargeRemovals(ctx, holder, definition, status);
        },
        formLost: holder => skipDeathDuty(ctx, holder, 'Form loss: saved status is removed; remaining form cleanup is not yet supported.'),
        specials: (holder, status) => {
            const item = statusItemId(status);
            if (item === HASTED_STATUS_ID) { effects.hasteRemoved(holder, status); return; }
            if (storedStatusHasSpecialTeardown(ctx, status)
                && !new Set<string>([STATUS_IDS.grappled, STATUS_IDS.grappling, STATUS_IDS.restrained, STATUS_IDS.concentrating,
                    STATUS_IDS.summon_tag, STATUS_IDS.resting, STATUS_IDS.riding, STATUS_IDS.ridden, STATUS_IDS.piloting, STATUS_IDS.piloted]).has(item))
                skipDeathDuty(ctx, holder, `${String(readItemField(effects.definition(status), 'item_name') ?? 'Status')} ended; its remaining cleanup is not yet supported.`);
        },
        rottingRemoved: holderId => {
            const holder = ctx.members.get(holderId);
            if (holder) skipDeathDuty(ctx, holder, 'Rotting ended; corpse inventory cleanup is not yet supported.');
        },
        hasteRemoved: (holder, status) => {
            if (statusItemId(status) !== HASTED_STATUS_ID || !record(ctx.encounter.campaign_arr)) return;
            const statusId = String(statusScalar(status, 'status_id'));
            if (hasteHandled.has(statusId)) return;
            hasteHandled.add(statusId);
            const ability: Row = { items: effects.definition(status).items, item_layer_types: {
                duration_int: 1, duration_type: 'rounds', effect_ends_on_target_end_of_turn: 1 } };
            const ends = getStatusEffectDurationTimes(ctx, ability, holder);
            // The expiry consequence outlives the connection which just ended.
            applyStatusEffect(ctx, { casterId: ecId(holder), targetIds: [ecId(holder)], abilityId: HASTED_STATUS_ID,
                rawDiceMod: 0, rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null },
                ability, STUNNED_STATUS_ID, 'status_effect_applied', holder, holder, ends, true, true, 0, false,
                String(statusScalar(status, 'status_origin_id') ?? ''));
        },
        summonsRemoved: status => {
            for (const summon of summonsOnStatusRemoved(status, historical, effects.rows(), [...ctx.members.values()], holders)) {
                if (Number(ecLean(summon, 'encounter_creature_dead') ?? 0) !== 0
                    || Number(ecLean(summon, 'encounter_creature_deleted') ?? 0) !== 0) continue;
                sourceSummonDeparture(ctx, summon, () => depart(summon));
            }
        },
    });
    const depart = (summon: Row, options: { departedAlive?: boolean; rosterDelete?: boolean; storageCleanup?: boolean } = {}): void => {
        const departureResources = resources ?? deathResourcesFor(ctx, summon);
        const summonId = ecId(summon), departure = planDepartureRows(summon, holders, options);
        if (ctx.spawnTurnTransition?.next_id === summonId) handBack('death:turn-successor-departure-not-modeled');
        if (ctx.read.turnDeath) {
            const turn = ctx.read.turnDeath(ctx.encounter, ctx.members, summonId);
            if (turn.kind === 'handback') handBack(turn.reason);
            if (turn.kind === 'transition') ctx.turnTransition = turn.transition;
            const start = plan.writes.length;
            settleTurnDeath(ctx, plan, summonId);
            // This boundary runs after the shared physical ledger was created. Let the
            // same removal packet own its expiries and retire their stale ledger copies.
            const boundary = plan.writes.splice(start);
            for (const write of boundary) {
                const physical = write.table === 'statuses'
                    ? effects.rows().find(row => statusScalar(row, 'status_id') === write.row.status_id) : null;
                if (physical) executor.retire(physical);
                else plan.writes.push(write);
            }
        } else if (String(encounterField(ctx.encounter, 'current_encounter_creature_id_turn') ?? '').toUpperCase() === summonId
            && Number(encounterField(ctx.encounter, 'in_combat') ?? 0) === 1) handBack('death:turn-reader-not-held');
        const connections = dyingStatusConnections(summon, historical);
        Object.assign(ec(summon), { encounter_creature_deleted: 1 }, options.departedAlive ? {} : { encounter_creature_dead: 1, v_current: 0, v_temp: 0 });
        if (!options.departedAlive) releaseDyingCasterStatuses(summon, [...ctx.members.values()], departureResources.casterDeathStatusIds, executor);
        if (!options.departedAlive) releaseMaintainedSources(summon);
        if (!options.departedAlive && !options.rosterDelete) settleSacrifice(ctx, summon, executor);
        // Native soft deletion is target-side bulk retirement, with no ordinary loss hooks.
        for (const row of [...effects.targetStatuses(summonId)]) {
            if (!laneOptions.transportHandled && [STATUS_IDS.riding, STATUS_IDS.ridden, STATUS_IDS.piloting, STATUS_IDS.piloted].some(id => id === statusItemId(row)))
                skipDeathDuty(ctx, summon, 'Summon transport ended; remaining support cleanup is not yet supported.');
            executor.retire(row);
        }
        endOrphanedConcentration(connections, executor);
        // Keep later callbacks on the same action's current inventory/status projections.
        plan.writes.push(...departure.writes.filter(write => {
            if (write.table !== 'statuses') return true;
            const physical = effects.rows().find(row => statusScalar(row, 'status_id') === write.row.status_id);
            if (physical) executor.retire(physical);
            return false; // The shared removal packet owns this row's single conditional save.
        }));
        for (const holder of holders) {
            const inventoryId = readInventoryField(holder, 'inventory_id');
            const write = departure.writes.find(write => write.table === 'inventory' && write.row.inventory_id === inventoryId);
            if (write && record(holder.inventory)) Object.assign(holder.inventory as Row, write.row);
        }
        if (!options.departedAlive && summonControllerId(summon)) triggerSummonDeath(ctx, summon);
    };
    const executor = createDeathStatusExecutor(effects);
    const releaseMaintainedSources = (holder: Row): void => {
        if (!summonControllerId(holder) || String(ecLean(holder, 'item_type') ?? '') !== 'creature') return;
        const statuses = [...effects.targetStatuses(ecId(holder))];
        for (const status of statuses) {
            const source = statusRemovalBefore(status, ecId(holder));
            const ownTag = statuses.some(tag => statusItemId(tag) === STATUS_IDS.summon_tag
                && statusScalar(tag, 'status_connected_id') === source.status_connected_id
                && statusScalar(tag, 'status_origin_id') === statusItemId(status)
                && statusRemovalBefore(tag, ecId(holder)).status_caster_id === source.status_caster_id);
            if (ownTag && ctx.read.entries(effects.definition(status), 'maintained_by_ability').length)
                executor.removeConnection(ecId(holder), String(statusScalar(status, 'status_id')));
        }
    };
    return { holders, historical, effects, executor, depart, releaseMaintainedSources };
}

/** handle_summon_tag_status_effect's selection, once per removal: the live summons a removed row reaches (its own tag, a retired
 * sibling's, or for a non-tag source its caster + origin spell's live tags). `before` is the status ledger before the removal. */
export function removalSummons(ctx: JanusCtx, removed: Row, before: readonly Row[]): Row[] {
    const holders = deathHolders([ctx.encounter, ...ctx.members.values()]);
    return summonsOnStatusRemoved(removed, before, deathStatusRows(holders), [...ctx.members.values()], holders);
}

/** A summon whose tag a removal outside a death retired (End Concentration, a GM delete, a failed concentration save) departs as a
 * dying caster's summons do: destroy_creature's turn settlement, holdings and the controller's summon-death hook; no kill row, no menace. */
export function departSummon(ctx: JanusCtx, summon: Row, before: readonly Row[]): void {
    sourceSummonDeparture(ctx, summon, () => {
        const plan = emptySpawnPlan();
        deathLane(ctx, deathResourcesFor(ctx, summon), plan, before).depart(summon);
        ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, plan);
    });
}

/** Maintenance and expiry use the same native connection retirement and recursive departure as death. */
export function removeStatusConnection(ctx: JanusCtx, status: Row): void {
    const plan = emptySpawnPlan();
    const { executor } = deathLane(ctx, undefined, plan);
    executor.removeConnection(String(ctx.read.value(status, 'status_target_id') ?? ''), String(ctx.read.value(status, 'status_id') ?? ''));
    if (plan.writes.length) ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, plan);
}

/** One actual-outcome death tail. Call at the native death point, before on-kill reactions.
 * The action's existing transaction owns every emitted row; there is no follow-up request. */
export function composeActualDeath(ctx: JanusCtx, caster: Row, target: Row, ability: Row, applied: number,
    options: { killMessage?: string; creditKiller?: boolean; sourceExpiry?: boolean } = {}): void {
    const id = ecId(target);
    if (ctx.composedDeaths?.includes(id)) return;
    if (ctx.dryRun && !options.sourceExpiry) { (ctx.composedDeaths ??= []).push(id); return; }
    if (options.creditKiller !== false) grantControllerMenace(ctx, caster, target);
    const resources = deathResourcesFor(ctx, target);
    const classification = classifyKillOrVanish(target, applied, { ...ctx.read, creatureSource: resources.creatureSource }, options.killMessage, options.sourceExpiry);
    const plan = emptySpawnPlan();
    if (String(encounterField(ctx.encounter, 'current_encounter_creature_id_turn') ?? '').toUpperCase() === id
        && Number(encounterField(ctx.encounter, 'in_combat') ?? 0) === 1 && !ctx.read.turnDeath) handBack('death:turn-reader-not-held');
    if (ctx.read.turnDeath) {
        const turn = ctx.read.turnDeath(ctx.encounter, ctx.members, id);
        if (turn.kind === 'handback') handBack(turn.reason);
        if (turn.kind === 'transition') ctx.turnTransition = turn.transition;
        settleTurnDeath(ctx, plan, id);
    }
    // Native kill/destroy marks the row after turn settlement, before any status-loss hook.
    Object.assign(ec(target), { v_current: 0 }, classification.changes);
    patchVitalityCluster(target, 0);
    const { historical, effects, executor, depart, releaseMaintainedSources } = deathLane(ctx, resources, plan);
    // Source ownership must not depend on whether the tag precedes the source in a target projection.
    releaseMaintainedSources(target);
    if (isOrdinarySummon(target)) {
        depart(target);
        ctx.rows.push(...classification.rows);
        ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, plan);
        (ctx.composedDeaths ??= []).push(id);
        return;
    }
    if (isHunterSummon(target))
        skipDeathDuty(ctx, target, 'Hunter recall is not yet supported; the actual death is saved.');
    const swarm = ctx.read.has(target, 'swarm') || ctx.read.has(target, 'swarm_size');
    if (swarm) skipDeathDuty(ctx, target, 'Swarm final body shedding is not yet supported; no linked body is minted.');
    resolveDeathStatusCleanup(target, [...ctx.members.values()], resources.casterDeathStatusIds,
        historical, executor, dying => settleSacrifice(ctx, dying, executor));
    if (historical.some(status => statusItemId(status) === STATUS_IDS.concentrating && statusScalar(status, 'status_target_id') === id)
        && !effects.targetStatuses(id).some(status => statusItemId(status) === STATUS_IDS.concentrating)
        && !ctx.rows.some(row => row.roll_type_1 === 'concentration_broken' && row.roll_by === id)) executor.concentrationBroken(id);
    if (classification.vanished) {
        // Deleted rows travel through the typed saver; ecChanges intentionally exposes only live scalars.
        plan.writes.push({ table: 'encounter_creatures', op: 'update', row: { encounter_creature_id: id, encounter_creature_deleted: 1 },
            before: { encounter_creature_deleted: Number(ecLean(ctx.originals.get(id) ?? target, 'encounter_creature_deleted') ?? 0) } });
    }
    const input: JanusInput = { casterId: ecId(caster), targetIds: [id], abilityId: String(readItemField(ability, 'item_id') ?? ''),
        rawDiceMod: 0, rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null };
    const previousConnection = ctx.statusConnectedId;
    // Posture, decay and PC Maimed share this death's connection, separate from the triggering zone.
    ctx.statusConnectedId = ctx.read.uuid();
    if (!classification.vanished) {
        const type = readLayerListEntryUuid(ctx.read.value(target, 'creature_type'));
        const bodyStatuses = [PRONE, ...(fromBaseUuid(type) && type !== UNDEAD ? [ROTTING] : [])];
        const failure = bodyStatuses.map(item => statusCapabilityFailure(ctx.read.definition(item), item)).find(Boolean);
        if (failure) plan.logs!.push({ kind: 'janus_unported_skip', actor_id: id,
            name: deathDutyName(target), detail: `Death posture/decay statuses: ${failure}` });
        else {
            applyStatusEffect(ctx, input, ability, PRONE, 'status_effect_applied', target, target,
                PERMANENT, false, true, 0, false, null, true);
            if (fromBaseUuid(type) && type !== UNDEAD) {
                const duration = getStatusEffectDurationTimes(ctx, { item_layer_types: { duration_int: 1, duration_type: 'days' } }, target);
                applyStatusEffect(ctx, input, ability, ROTTING, 'status_effect_applied', target, target, duration, false, true, 0, false, null);
            }
        }
    }
    dropHands(ctx, target, plan, resources.drop);
    const merchant = planMerchantDrops(target, merchantDeathPictures(target, ctx.encounter), { value: (row, key) => ctx.read.value(row, key) }, ctx.roll);
    let rows = mergeSpawnPlans(plan, merchant.plan);
    ctx.rows.push(...merchant.rows);
    if (!classification.vanished) {
        const landing = resources.land!(target).changes;
        Object.assign(ec(target), landing);
        if (landing.z_pos !== undefined) target.position = { ...record(target.position), z_pos: landing.z_pos };
        if (record(target.player_characters)) applyStatusEffect(ctx, input, ability, 'A5CDC77C-D224-DD5F-291B-EF8DF2EA443D',
            'status_effect_applied', caster, target, PERMANENT);
        if (!swarm) {
            const bits = planMonsterBits(target, false, { ...resources.inventory,
                value: (row, key) => ctx.read.value(row, key), entries: (row, key) => ctx.read.entries(row, key) });
            rows = mergeSpawnPlans(rows, bits.plan);
            ctx.rows.push(...bits.rows);
            const corpse = planCorpseMint(target, caster, [...ctx.members.values()], readInventoryRows(ctx.encounter, 'encounter_inventories_arr'),
                { ...resources.inventory, depthAuthored: !!encounterField(ctx.encounter, 'encounter_shape'),
                    value: (row, key) => ctx.read.value(row, key), entries: (row, key) => ctx.read.entries(row, key) });
            rows = mergeSpawnPlans(rows, corpse.plan);
            if (corpse.grapple) executor.removeTarget(id, String(statusScalar(corpse.grapple.status, 'status_id')));
        }
    }
    ctx.rows.push(...classification.rows);
    ctx.statusConnectedId = previousConnection;
    ctx.spawnRows = mergeSpawnPlans(ctx.spawnRows, ctx.dryRun ? rows : resources.prepareRows?.(rows) ?? rows);
    (ctx.composedDeaths ??= []).push(id);
}
